import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Events() {
  const { events, refreshEvents, setEventId } = useOutletContext();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    partner_one: "",
    partner_two: "",
    start_date: "",
    end_date: "",
  });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Event name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/events", form);
      await refreshEvents();
      setEventId(data.id);
      setForm({ name: "", partner_one: "", partner_two: "", start_date: "", end_date: "" });
      setCreating(false);
      toast.success("Event created");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete “${name}” and all its guests, rooms, and media?`)) return;
    try {
      await api.delete(`/events/${id}`);
      const list = await refreshEvents();
      setEventId((list[0] && list[0].id) || null);
      toast.success("Event deleted");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Events</h1>
          <p className="mt-1 text-sm text-[#525252]">Create and manage celebrations</p>
        </div>
        <Button
          onClick={() => setCreating((v) => !v)}
          className="bg-[#1A362D] hover:bg-[#12271f]"
          data-testid="create-event-toggle"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Event
        </Button>
      </div>

      {creating && (
        <form onSubmit={create} className="space-y-4 rounded-md border border-[#E5E5E5] bg-white p-6">
          <div className="space-y-2">
            <Label>Event name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-event-name"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Partner one</Label>
              <Input
                value={form.partner_one}
                onChange={(e) => setForm({ ...form, partner_one: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Partner two</Label>
              <Input
                value={form.partner_two}
                onChange={(e) => setForm({ ...form, partner_two: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Start date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>End date</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="create-event-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create event"}
          </Button>
        </form>
      )}

      <div className="divide-y divide-[#E5E5E5] rounded-md border border-[#E5E5E5] bg-white">
        {(events || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-[#525252]">No events yet. Create one above.</div>
        ) : (
          events.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between gap-4 px-5 py-4">
              <div>
                <div className="font-medium">{ev.name}</div>
                <div className="text-xs text-[#525252]">
                  {[ev.partner_one, ev.partner_two].filter(Boolean).join(" & ") || "—"}
                  {ev.start_date ? ` · ${ev.start_date}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEventId(ev.id)}>
                  Manage
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(ev.id, ev.name)}
                  className="text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid={`delete-event-${ev.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
